import {
  Button,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { Reorder, useMotionValue } from 'motion/react'
import { useContext, useState } from 'react'
import { FileWithPath } from 'react-dropzone'
import previewIcon from '~/assets/icons/icon-search.svg?react'
import attachFileIcon from '~/assets/svgs/icon-attach-file.svg?react'
import trashIcon from '~/assets/svgs/icon-trash.svg?react'
import {
  DropzoneVariant,
  useDropzoneStyles,
} from '~/components/shared/componentStyles/Dropzone.style'
import { formatFileSize } from '~/utils/common'

import { Context } from './Context'
import { FileWithHashKey } from './types'

interface FilePreviewModalProps {
  isOpen: boolean
  onClose: () => void
  fileUrl: string | null
  fileName: string
}

const acceptedFileStyle = {
  alignItems: 'center',
  background: '#fff',
  borderRadius: '5px',
  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)',
  marginBottom: '4px',
  display: 'flex',
  justifyContent: 'space-between',
  padding: '8px',
  width: '100%',
}
const FilePreviewModal = ({
  isOpen,
  onClose,
  fileUrl,
  fileName,
}: FilePreviewModalProps) => {
  const isImage = fileName.match(/\.(jpeg|jpg|gif|png|webp|bmp)$/i) !== null

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent maxW="90vw" maxH="90vh">
        <ModalHeader>{fileName}</ModalHeader>
        <ModalCloseButton />
        <ModalBody
          p={0}
          display="flex"
          justifyContent="center"
          alignItems="center"
          maxH="80vh"
          overflow="auto"
        >
          {fileUrl && isImage ? (
            <img
              src={fileUrl}
              alt={fileName}
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                margin: 'auto',
              }}
              draggable="false"
            />
          ) : (
            fileUrl && (
              <iframe
                src={fileUrl}
                style={{
                  width: '100%',
                  height: '80vh',
                  border: 'none',
                }}
                title={fileName}
                draggable="false"
              />
            )
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default function ContentAcceptedMulti() {
  const { acceptedFiles, coverPage, setFiles } = useContext(Context)
  const handleOnReorder = (items: FileWithHashKey[]) => {
    setFiles({ accepted: items })
  }
  return (
    <VStack
      justify="center"
      paddingBottom="12px"
      paddingTop="12px"
      spacing={2}
      width="100%"
      height="fit-content"
    >
      <Reorder.Group
        axis="y"
        onReorder={handleOnReorder}
        values={acceptedFiles}
        style={{ width: '100%', paddingLeft: 0 }}
      >
        {coverPage && <CoverPage file={coverPage} />}
        {acceptedFiles.map((file) => (
          <DraggableItem file={file} key={`${file.name}-${file.hashKey}`} />
        ))}
      </Reorder.Group>
    </VStack>
  )
}
function CoverPage({ file }: { file: FileWithPath }) {
  const { handleCoverPageDelete } = useContext(Context)
  const styles = useDropzoneStyles({
    variant: DropzoneVariant.FileAccepted,
  })
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const previewFile = () => {
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    onOpen()
  }

  const removeItem = () => {
    handleCoverPageDelete?.()
  }

  return (
    <>
      <li style={acceptedFileStyle}>
        <Icon sx={styles.icon} as={attachFileIcon} />
        <Text
          sx={{
            ...styles.text,
            flex: 1,
            margin: 'auto',
            textAlign: 'center',
          }}
        >
          {file.name} ({formatFileSize(file.size)})
        </Text>
        <Button size="sm" variant="ghost" onClick={previewFile}>
          <Icon as={previewIcon} />
        </Button>
        <Button size="sm" variant="ghost" onClick={removeItem}>
          <Icon as={trashIcon} />
        </Button>
      </li>

      <FilePreviewModal
        isOpen={isOpen}
        onClose={() => {
          onClose()
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl)
            setPreviewUrl(null)
          }
        }}
        fileUrl={previewUrl}
        fileName={file.name}
      />
    </>
  )
}
function DraggableItem({ file }: { file: FileWithHashKey }) {
  const {
    acceptedFiles,
    potentialDuplicatePaths,
    props: { maxFileSize },
    setCurrentVariant,
    setFiles,
  } = useContext(Context)
  const isCoverPage = file.name === 'Cover Page.pdf'
  const [isDragging, setIsDragging] = useState(false)
  const y = useMotionValue(0)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const styles = useDropzoneStyles({
    variant: DropzoneVariant.FileAccepted,
  })

  const isPotentialDuplicate =
    file.path && potentialDuplicatePaths.includes(file.path)

  function removeItem() {
    // Find the file by reference (exact same object) rather than by name
    const fileIndex = acceptedFiles.findIndex((f) => f === file)

    // Create a new array without that specific file
    const filesNext = [...acceptedFiles]
    if (fileIndex !== -1) {
      filesNext.splice(fileIndex, 1)
    }

    setFiles({ accepted: filesNext })
    if (!filesNext.length) {
      setCurrentVariant(DropzoneVariant.Default)
    }
  }

  const previewFile = () => {
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    onOpen()
  }

  const handleDragEnd = () => {
    setIsDragging(false)
  }
  const handleDragStart = () => {
    setIsDragging(true)
  }
  return (
    <>
      <Reorder.Item
        disabled={isCoverPage}
        draggable={!isCoverPage}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        value={file}
        id={file.hashKey || file.name}
        style={{
          ...acceptedFileStyle,
          y,
          cursor: 'grab',
          boxShadow: isDragging
            ? '5px 5px 10px rgba(0,0,0,0.3)'
            : '0px 1px 3px rgba(0, 0, 0, 0.2)',
        }}
      >
        <Icon sx={styles.icon} as={attachFileIcon} />
        <Text
          sx={{
            ...styles.text,
            ...(isPotentialDuplicate && { color: 'orange' }),
            ...(file.size > maxFileSize && { color: 'red' }),
            flex: 1,
            margin: 'auto',
            textAlign: 'center',
          }}
        >
          {file.name} ({formatFileSize(file.size)})
        </Text>
        <Button size="sm" variant="ghost" onClick={previewFile}>
          <Icon as={previewIcon} />
        </Button>
        <Button size="sm" variant="ghost" onClick={removeItem}>
          <Icon as={trashIcon} />
        </Button>
      </Reorder.Item>

      <FilePreviewModal
        isOpen={isOpen}
        onClose={() => {
          onClose()
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl)
            setPreviewUrl(null)
          }
        }}
        fileUrl={previewUrl}
        fileName={file.name}
      />
    </>
  )
}
